/**
 * Central API configuration for the Orbiee frontend.
 *
 * Four backends, one base URL each, and every endpoint below is built from
 * one of them — so a deployment moves by setting four environment variables
 * (see .env.example) and nothing else:
 *
 *   REACT_APP_API_BASE_URL             Cloud Functions — product listing and
 *                                      search, sign-up, and the admin calls
 *                                      the admin service has no endpoint for
 *                                      (edit user, create user, enrich).
 *   REACT_APP_ORBIEE_BACKEND_BASE_URL  Orbiee FastAPI on Cloud Run — the agent
 *                                      WebSocket, auth, cart, wishlist, the
 *                                      realtime avatar, virtual try-on.
 *   REACT_APP_ORBIEE_COMMERCE_BASE_URL Orbiee Commerce API on Cloud Run — the
 *                                      catalogue browse endpoints (categories,
 *                                      accessories, sale, new arrivals,
 *                                      featured), filters, product detail,
 *                                      profile and orders. Took these over
 *                                      from the Cloud Functions on 2026-10-20
 *                                      (the "FIX the APIs" list).
 *   REACT_APP_ORBIEE_ADMIN_BASE_URL    Orbiee Admin Features API on Cloud Run —
 *                                      the admin user list, product list and
 *                                      search, product update, user
 *                                      deactivation. Took these over from the
 *                                      Cloud Functions on 2026-10-27 (the
 *                                      "Admin_features_apis" list).
 *
 * Each is read through envValue(): the value set on the running container
 * (Cloud Run --set-env-vars, via env-config.js) wins, then the build-time
 * value CRA inlined, then the dev default below — exactly what the app ran
 * against while these URLs were still literals in the code (made configurable
 * 2026-10-09; runtime added 2026-10-12). Only REACT_APP_* variables reach the
 * bundle, and the bundle ships to every visitor: never a secret here.
 */

import { envValue } from "./runtimeEnv";

const DEFAULT_BASE_URL =
  "https://us-central1-retail-jkommerce-poc.cloudfunctions.net";

const DEFAULT_ORBIEE_BACKEND_BASE_URL =
  "https://orbiee-backend-dev-218142584790.us-central1.run.app";

const DEFAULT_ORBIEE_COMMERCE_BASE_URL =
  "https://orbiee-commerce-mock-218142584790.us-central1.run.app";

const DEFAULT_ORBIEE_ADMIN_BASE_URL =
  "https://orbiee-admin-218142584790.us-central1.run.app";

/** Stripped once here so `${base}/path` can never come out as `//path`. */
const stripTrailingSlash = (url) => url.replace(/\/+$/, "");

/** Backend base URL (Cloud Functions) */
export const API_BASE_URL = stripTrailingSlash(
  envValue("REACT_APP_API_BASE_URL", process.env.REACT_APP_API_BASE_URL) ||
    DEFAULT_BASE_URL
);

/** Orbiee FastAPI backend (agent WebSocket, auth, cart, wishlist, avatar, try-on) */
export const ORBIEE_BACKEND_BASE_URL = stripTrailingSlash(
  envValue(
    "REACT_APP_ORBIEE_BACKEND_BASE_URL",
    process.env.REACT_APP_ORBIEE_BACKEND_BASE_URL
  ) || DEFAULT_ORBIEE_BACKEND_BASE_URL
);

/** Orbiee Commerce API (catalogue browse, filters, product detail, profile, orders) */
export const ORBIEE_COMMERCE_BASE_URL = stripTrailingSlash(
  envValue(
    "REACT_APP_ORBIEE_COMMERCE_BASE_URL",
    process.env.REACT_APP_ORBIEE_COMMERCE_BASE_URL
  ) || DEFAULT_ORBIEE_COMMERCE_BASE_URL
);

/** Orbiee Admin Features API (admin users, product list/search/update) */
export const ORBIEE_ADMIN_BASE_URL = stripTrailingSlash(
  envValue(
    "REACT_APP_ORBIEE_ADMIN_BASE_URL",
    process.env.REACT_APP_ORBIEE_ADMIN_BASE_URL
  ) || DEFAULT_ORBIEE_ADMIN_BASE_URL
);

/** `base` + `path`, with or without a leading slash on the path. */
const joinUrl = (base, path) =>
  `${base}${path.startsWith("/") ? path : `/${path}`}`;

/** Helper to build full URL from path (e.g. "/login_v2" or "login_v2") */
export const apiUrl = (path) => joinUrl(API_BASE_URL, path);

/** Same, on the Orbiee backend. */
export const apiUrl_backend = (path) => joinUrl(ORBIEE_BACKEND_BASE_URL, path);

/** Same, on the Orbiee Commerce API. */
export const apiUrl_commerce = (path) => joinUrl(ORBIEE_COMMERCE_BASE_URL, path);

/** Same, on the Orbiee Admin Features API. */
export const apiUrl_admin = (path) => joinUrl(ORBIEE_ADMIN_BASE_URL, path);

/**
 * Full WS URL for `/ws/chat/{userId}?device_id={deviceId}` (optional `&` query
 * from caller). Built on ORBIEE_BACKEND_BASE_URL so it moves with the
 * environment — until 2026-10-09 it was a literal dev URL that ignored every
 * variable. The scheme stays the backend's `https://` on purpose: that is
 * exactly what the working code sent, and `new WebSocket()` accepts http(s)
 * URLs and upgrades them to ws(s). Switching to `wss://` is a separate decision.
 */
export function orbieeChatWsUrl(userId, deviceId) {
  const path = `/ws/chat/${encodeURIComponent(String(userId))}?device_id=${encodeURIComponent(String(deviceId))}`;
  return `${ORBIEE_BACKEND_BASE_URL}${path}`;
}

/** Always use WebSocket chat — Cloud Function fallback removed. */
export const USE_ORBIEE_WEBSOCKET_CHAT = true;

/**
 * Frontend ↔ Agent UI sync (`ui_event` frames + `screen_context` on
 * start/voice). Set REACT_APP_UI_AGENT_SYNC=false to disable without
 * touching code (kill switch if the backend contract is not deployed).
 */
export const UI_AGENT_SYNC_ENABLED =
  envValue("REACT_APP_UI_AGENT_SYNC", process.env.REACT_APP_UI_AGENT_SYNC) !==
  "false";

// ======================
// Backend API paths (full URLs via apiUrl() / apiUrl_backend())
// ======================

/** Auth */
export const LOGIN_URL = apiUrl("login_v2");
/** Orbiee agent: POST /api/auth/logout (Bearer) — ends credential + conversation session */
export const LOGOUT_URL = apiUrl_backend("/api/auth/logout");
/** Orbiee agent: POST /auth_validate (Bearer Firebase ID token) — confirms the sign-in and returns the profile */
export const AUTH_VALIDATE_URL = apiUrl_backend("/auth_validate");

/** Avatar & realtime */
export const AVATAR_REALTIME_URL = apiUrl_backend("/avatar_realtime");

export const CHAT_AVATAR_URL = apiUrl("chat_avatar");

/** Chat / AI */
export const TTS_URL = apiUrl("tts_function");
export const STT_URL = apiUrl("stt_function");

/**
 * Orbiee Commerce API (2026-10-20). These moved off the Cloud Functions named
 * in the trailing comments, per the "FIX the APIs" list; methods and shapes
 * are the Commerce service's (its OpenAPI is served at `<base>/openapi.json`).
 * Where a Cloud Function had a `_dev` twin, one Commerce endpoint replaces
 * both, so the `*_DEV_URL` constants are gone.
 */
/** Catalogue browse — all GET, no body */
export const PRODUCT_CATEGORY_URL = apiUrl_commerce("product_category"); // was product_category(_dev)
export const PRODUCT_ACCESSORIES_URL = apiUrl_commerce("product_accessories"); // was product_accessories(_dev)
export const PRODUCT_ONSALE_URL = apiUrl_commerce("product_onsale"); // was product_onsale(_dev)
export const NEW_ARRIVALS_URL = apiUrl_commerce("new_arrivals"); // was new_arrivals_dev; same ?page=&page_size=
export const FEATURED_PRODUCTS_URL = apiUrl_commerce("featured-products"); // was featured_products (POST); GET, no paging
export const FILTER_OPTIONS_URL = apiUrl_commerce("filter_options");
/** One sub-category's products — POST { gender, category, subcategory,
    on_sale?: "yes", colour filters… } → an array of listing items, the same
    rows the Cloud Function products_v1_dev returned (2026-10-28). The "FIX
    the APIs" list spells the path `product_sub_cat`, which answers 404; the
    service's OpenAPI path is the plural. */
export const PRODUCT_SUB_CAT_URL = apiUrl_commerce("products_sub_cat");
/** Product detail — POST { product_id, visitor_id } (was general_detail_page) */
export const PRODUCT_DETAIL_URL = apiUrl_commerce("product_detail");
/** Profile — GET /customer_profile/{customer_id} (was POST customer_profile { customer_id }) */
export const customerProfileUrl = (customerId) =>
  apiUrl_commerce(`customer_profile/${encodeURIComponent(String(customerId))}`);
export const CUSTOMER_PROFILE_UPDATE_URL = apiUrl_commerce("customer_profile_update");
/** Orders — POST { customer_id } → the customer's order lines (was customer_orders_v1) */
export const CUSTOMER_ORDERS_URL = apiUrl_commerce("customer_orders");

/** Products & catalog (Cloud Functions) */
export const PRODUCTS_V1_URL = apiUrl("products_v1");
export const DETAIL_PAGE_VIEW_URL = apiUrl("detail_page_view");
export const VIRTUAL_TRY_ON_URL = apiUrl_backend("/virtual_try_on");

/** Home (Cloud Functions). The landing page's `promotions` and
    `recommended_for_you` were retired on 2026-10-22 — it shows the featured
    grid to everyone — and `complete_the_look` with them. */
export const HOME_PAGE_URL = apiUrl("home_page");

/** Search (Cloud Functions) */
export const SEARCH_PRODUCTS_URL = apiUrl("search_products");

/** Cart — legacy Cloud Function (deprecated; kept for reference) */
export const CART_OPERATIONS_URL = apiUrl("cart_operations");

/** Cart — REST API (GET/POST/PUT/DELETE /api/cart) */
export const CART_API_URL = apiUrl_backend("/api/cart");

/** Wishlist — legacy Cloud Function (deprecated; kept for reference) */
// export const WISHLIST_OPERATIONS_URL = apiUrl("wishlist_operations_dev");
export const WISHLIST_OPERATIONS_URL = "";

/** Wishlist — REST API (GET/POST/DELETE /api/wishlist) */
export const WISHLIST_API_URL = apiUrl_backend("/api/wishlist");

/**
 * Admin — Orbiee Admin Features API (2026-10-27). Moved off the Cloud
 * Functions of the same name, per the "Admin_features_apis" list; methods and
 * shapes are the service's (OpenAPI at `<base>/openapi.json`). The list spells
 * two paths `update_products` / `delete_users`; the service answers those 404
 * and serves the singular ones below.
 */
export const USERS_URL = apiUrl_admin("users"); // was POST {}; GET, no body
export const GET_ALL_PRODUCTS_URL = apiUrl_admin("get_all_products"); // same ?page=&page_size=
export const PRODUCT_SEARCH_URL = apiUrl_admin("product-search"); // was product_search; now honours ?page=&page_size=
export const UPDATE_PRODUCT_URL = apiUrl_admin("update_product");
export const DELETE_USER_URL = apiUrl_admin("delete_user"); // deactivates (isActive: false), keeps the record

/** Admin — Cloud Functions; the admin service has no endpoint for these. */
export const UPDATE_USER_PREFERENCES_V1_URL = apiUrl(
  "update_user_preferences_v1"
);
export const ENRICH_PRODUCT_URL = apiUrl("enrich_product");
export const UPDATE_ENRICH_PRODUCT_URL = apiUrl("update_enrich_product");

/** Create user (auth flow) */
export const CREATE_USER_V1_URL = apiUrl("create_user_v1");
/** Sign-up form (Cloud Function) */
export const ADD_USER_URL = apiUrl("add_user");

// ======================
// External APIs
// ======================

export const ELEVENLABS_SPEECH_TO_TEXT_URL =
  "https://api.elevenlabs.io/v1/speech-to-text";

export const D_ID_API_BASE = "https://api.d-id.com";

// ======================
// Default export (all config)
// ======================

const apiConfig = {
  API_BASE_URL,
  ORBIEE_BACKEND_BASE_URL,
  ORBIEE_COMMERCE_BASE_URL,
  ORBIEE_ADMIN_BASE_URL,
  orbieeChatWsUrl,
  USE_ORBIEE_WEBSOCKET_CHAT,
  UI_AGENT_SYNC_ENABLED,
  apiUrl,
  apiUrl_backend,
  apiUrl_commerce,
  apiUrl_admin,
  LOGIN_URL,
  LOGOUT_URL,
  AUTH_VALIDATE_URL,
  AVATAR_REALTIME_URL,
  CHAT_AVATAR_URL,
  TTS_URL,
  STT_URL,
  PRODUCT_CATEGORY_URL,
  PRODUCT_ACCESSORIES_URL,
  PRODUCT_ONSALE_URL,
  NEW_ARRIVALS_URL,
  FEATURED_PRODUCTS_URL,
  FILTER_OPTIONS_URL,
  PRODUCT_DETAIL_URL,
  customerProfileUrl,
  CUSTOMER_PROFILE_UPDATE_URL,
  CUSTOMER_ORDERS_URL,
  PRODUCTS_V1_URL,
  PRODUCT_SUB_CAT_URL,
  DETAIL_PAGE_VIEW_URL,
  VIRTUAL_TRY_ON_URL,
  HOME_PAGE_URL,
  PRODUCT_SEARCH_URL,
  SEARCH_PRODUCTS_URL,
  USERS_URL,
  CART_OPERATIONS_URL,
  CART_API_URL,
  WISHLIST_OPERATIONS_URL,
  WISHLIST_API_URL,
  UPDATE_USER_PREFERENCES_V1_URL,
  DELETE_USER_URL,
  GET_ALL_PRODUCTS_URL,
  ENRICH_PRODUCT_URL,
  UPDATE_ENRICH_PRODUCT_URL,
  UPDATE_PRODUCT_URL,
  CREATE_USER_V1_URL,
  ADD_USER_URL,
  ELEVENLABS_SPEECH_TO_TEXT_URL,
  D_ID_API_BASE,
};

export default apiConfig;
